import { dateRange, DateRange, DateRangeInstance } from './DateRange';
import { addDaysToDate, subtractDaysFromDate } from './dateUtils';

export type DateRanges<T extends Date | string = Date> = DateRange<T>[];

export interface DateRangesInstance {
  ranges: DateRangeInstance[];
  toDateRanges: () => DateRanges;
  addRanges: (rangesToAdd: DateRanges) => DateRangesInstance;
  isAnyOverlappingWith: (other: DateRangeInstance) => boolean;
  containsAnyOverlappingRanges: (maxDayDifferenceBetweenRanges?: number) => boolean;
  mergeOverlappingRanges: (maxDayDifferenceBetweenRanges?: number) => DateRangesInstance;
  excludeRange: (other?: DateRangeInstance) => DateRangesInstance;
  sortedByStartDate: () => DateRanges;
}

export const dateRanges = <T extends Date | string>(ranges: DateRanges<T>): DateRangesInstance => {
  const _ranges = ranges.map(dateRange);

  const toDateRanges = (): DateRanges => {
    return _ranges.map(range => range.toDateRange());
  };

  const addRanges = (rangesToAdd: DateRanges): DateRangesInstance => {
    return dateRanges([...toDateRanges(), ...rangesToAdd]);
  };

  const isAnyOverlappingWith = (other: DateRangeInstance): boolean => {
    return _ranges.some(range => range.isOverlappingWith(other));
  };

  const containsAnyOverlappingRanges = (maxDayDifferenceBetweenRanges = 1): boolean => {
    const sortedRanges = dateRanges(sortedByStartDate());

    for (let i = 0; i < sortedRanges.ranges.length - 1; i++) {
      const currentRange = sortedRanges.ranges[i];
      const nextRange = sortedRanges.ranges[i + 1];

      if (
        currentRange.isOverlappingWith(nextRange) ||
        currentRange.isConsecutiveTo(nextRange, maxDayDifferenceBetweenRanges)
      ) {
        return true;
      }
    }

    return false;
  };

  const mergeOverlappingRanges = (maxDayDifferenceBetweenRanges = 1): DateRangesInstance => {
    if (_ranges.length === 0 || _ranges.length === 1) return dateRanges(toDateRanges());

    const sortedRanges = dateRanges(sortedByStartDate());

    const mergedRanges: DateRanges = [];
    let currentMergedRange = sortedRanges.ranges[0];

    sortedRanges.ranges.slice(1).forEach(currentRange => {
      const isOverlapping = currentMergedRange.isOverlappingWith(currentRange);
      const isConsecutive = currentMergedRange.isConsecutiveTo(currentRange, maxDayDifferenceBetweenRanges);

      if (isOverlapping || isConsecutive) {
        currentMergedRange = currentMergedRange.extendRangeEndTo(currentRange);
      } else {
        mergedRanges.push(currentMergedRange.toDateRange());
        currentMergedRange = currentRange;
      }
    });

    mergedRanges.push(currentMergedRange.toDateRange());

    return dateRanges(mergedRanges);
  };

  const excludeRange = (other?: DateRangeInstance): DateRangesInstance => {
    if (!other) return dateRanges(toDateRanges());

    const rangesWithoutExcludeRange: DateRanges = [];

    for (const range of _ranges) {
      if (!range.isOverlappingWith(other)) {
        rangesWithoutExcludeRange.push(range.toDateRange());
        continue;
      }

      if (range.startsBeforeAndOverlapsWith(other)) {
        rangesWithoutExcludeRange.push({
          from: range.from,
          to: subtractDaysFromDate(other.from, 1),
        });
      }

      if (range.endsAfterAndOverlapsWith(other)) {
        rangesWithoutExcludeRange.push({
          from: addDaysToDate(other.to, 1),
          to: range.to,
        });
      }
    }

    return dateRanges(rangesWithoutExcludeRange);
  };

  const sortedByStartDate = (): DateRanges => {
    return [...toDateRanges()].sort((a, b) => a.from.getTime() - b.from.getTime());
  };

  return {
    ranges: _ranges,
    toDateRanges,
    addRanges,
    isAnyOverlappingWith,
    containsAnyOverlappingRanges,
    mergeOverlappingRanges,
    excludeRange,
    sortedByStartDate,
  };
};
